import { useEffect, useId, useState, useCallback, useMemo } from "react";
import { useProgramma } from "../ProgrammaShell";
import { portalQueries, readPortalCache, writePortalCache } from "@/client/lib/portal";
import { Button } from "@/client/components/ui/button";
import { Checkbox } from "@/client/components/ui/checkbox";
import { Notice } from "@/client/components/ui/notice";
import { CheckCircle2Icon, PlusIcon, Trash2Icon, InfoIcon, AlertCircleIcon } from "lucide-react";

type Selection = {
  id: number;
  offeringId: number;
  selectionStatusCode: string;
  selectionOriginCode: string;
  notes: string | null;
};

type Offering = {
  id: number;
  programmeId: number;
  providerOrganizationId: number;
  title: string;
  shortDescription: string | null;
};

type Programme = { id: number; title: string; shortDescription: string | null };
type Provider = { id: number; legalName: string; displayName: string | null };

// Dutch-aware, case-insensitive title sort for the training cards.
const titleCollator = new Intl.Collator("nl", { sensitivity: "base", numeric: true });

/**
 * Deskundigheidsbevordering — gratis trainingen voor het schoolteam / de leerkrachten.
 * School/team-level, net als Extra maatwerk: er is geen koppeling aan leerlinggroepen,
 * de school geeft alleen aan welke trainingen het team graag wil volgen. Modelled on
 * MaatwerkSection, gefilterd op de enrolment-mode "deskundigheidsbevordering".
 */
export function DeskundigheidsbevorderingSection({ schoolId }: { schoolId: number }) {
  const { primaryAction, setSaveState, setLastSavedAt, saveState } = useProgramma();
  const optOutId = useId();

  // Seed from the prefetched cache for an instant first paint; load() revalidates.
  const cachedSel = readPortalCache<{ selections?: Selection[]; sectionOptOuts?: string[] }>(portalQueries.selections(schoolId).queryKey);
  const cachedElig = readPortalCache<{ deskundigheidsbevorderingOfferings?: Offering[]; programmes?: Programme[]; providers?: Provider[] }>(portalQueries.eligible(schoolId).queryKey);
  const [selections, setSelections] = useState<Selection[]>(() => cachedSel?.selections ?? []);
  const [optedOut, setOptedOut] = useState<boolean>(() => (cachedSel?.sectionOptOuts ?? []).includes("deskundigheidsbevordering"));
  const [trainingOfferings, setTrainingOfferings] = useState<Offering[]>(() => cachedElig?.deskundigheidsbevorderingOfferings ?? []);
  const [programmes, setProgrammes] = useState<Programme[]>(() => cachedElig?.programmes ?? []);
  const [providers, setProviders] = useState<Provider[]>(() => cachedElig?.providers ?? []);
  const [loading, setLoading] = useState(() => !(cachedSel && cachedElig));
  const [error, setError] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [togglingOptOut, setTogglingOptOut] = useState(false);
  // Local toelichting drafts keyed by selection id
  const [notesDraft, setNotesDraft] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    try {
      setError(false);
      const [selectionsRes, eligibleRes] = await Promise.all([
        fetch(`/api/school/${schoolId}/selections`, { credentials: "include" }),
        fetch(`/api/school/${schoolId}/eligible`, { credentials: "include" }),
      ]);
      if (selectionsRes.ok) {
        const d = await selectionsRes.json();
        setSelections(d.selections ?? []);
        setOptedOut((d.sectionOptOuts ?? []).includes("deskundigheidsbevordering"));
        writePortalCache(portalQueries.selections(schoolId).queryKey, d);
      }
      if (eligibleRes.ok) {
        const d = await eligibleRes.json();
        setTrainingOfferings(d.deskundigheidsbevorderingOfferings ?? []);
        setProgrammes(d.programmes ?? []);
        setProviders(d.providers ?? []);
        writePortalCache(portalQueries.eligible(schoolId).queryKey, d);
      }
    } catch {
      setError(true);
    }
    setLoading(false);
  }, [schoolId]);

  useEffect(() => { load(); }, [load]);

  const saved = () => {
    setSaveState("saved");
    setLastSavedAt(new Date().toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }));
  };

  const offeringIdSet = useMemo(() => new Set(trainingOfferings.map((o) => o.id)), [trainingOfferings]);

  // School's training interests (not cancelled)
  const interests = useMemo(
    () => selections.filter(
      (s) => s.selectionOriginCode === "school_chosen" && s.selectionStatusCode !== "cancelled" && offeringIdSet.has(s.offeringId)
    ),
    [selections, offeringIdSet]
  );
  const interestByOffering = useMemo(() => {
    const m = new Map<number, Selection>();
    for (const s of interests) m.set(s.offeringId, s);
    return m;
  }, [interests]);

  const getProgramme = (id: number) => programmes.find((p) => p.id === id);
  const getProvider = (id: number) => providers.find((p) => p.id === id);

  // Cards alphabetically by their displayed title (programme title, falling back to offering title).
  const sortedOfferings = useMemo(() => {
    const titleOf = (o: Offering) => programmes.find((p) => p.id === o.programmeId)?.title ?? o.title;
    return [...trainingOfferings].sort((a, b) => titleCollator.compare(titleOf(a), titleOf(b)));
  }, [trainingOfferings, programmes]);

  const addInterest = async (offering: Offering) => {
    setBusyId(offering.id);
    setSaveState("saving");
    try {
      const res = await fetch(`/api/school/${schoolId}/selections`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ offeringId: offering.id, selectionStatusCode: "interested" }),
      });
      if (res.ok) { await load(); saved(); }
      else { setSaveState("error"); }
    } catch { setSaveState("error"); }
    setBusyId(null);
  };

  const removeInterest = async (selectionId: number) => {
    setBusyId(selectionId);
    setSaveState("saving");
    try {
      const res = await fetch(`/api/school/${schoolId}/selections/${selectionId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) { await load(); saved(); }
      else { setSaveState("error"); }
    } catch { setSaveState("error"); }
    setBusyId(null);
  };

  const saveNotes = async (selectionId: number, notes: string) => {
    setSaveState("saving");
    try {
      const res = await fetch(`/api/school/${schoolId}/selections/${selectionId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ notes }),
      });
      if (res.ok) { await load(); saved(); }
      else { setSaveState("error"); }
    } catch { setSaveState("error"); }
  };

  // Opt-out: "onze school neemt dit schooljaar geen deskundigheidsbevordering af." When turned ON,
  // we also intrekken any interests already aangegeven (an opt-out and a keuze are contradictory),
  // so the section reads as a clean "geen" answer. Turning it OFF re-opens the section.
  const toggleOptOut = async (next: boolean) => {
    if (togglingOptOut) return;
    setTogglingOptOut(true);
    setSaveState("saving");
    try {
      if (next) {
        // Withdraw any existing interests first, so opting out clears the section like a "geen".
        for (const sel of interests) {
          const r = await fetch(`/api/school/${schoolId}/selections/${sel.id}`, { method: "DELETE", credentials: "include" });
          if (!r.ok) { setSaveState("error"); setTogglingOptOut(false); return; }
        }
      }
      const res = await fetch(`/api/school/${schoolId}/section-optouts/deskundigheidsbevordering`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ optedOut: next }),
      });
      if (res.ok) { setOptedOut(next); await load(); saved(); }
      else { setSaveState("error"); }
    } catch { setSaveState("error"); }
    setTogglingOptOut(false);
  };

  if (loading) return <p className="text-sm text-muted-foreground">Laden...</p>;

  if (error) {
    return (
      <Notice
        tone="error"
        icon={<AlertCircleIcon />}
        title="Kan het trainingsaanbod niet laden"
        action={
          <Button variant="outline" size="sm" onClick={() => { setLoading(true); load(); }}>
            Opnieuw proberen
          </Button>
        }
      >
        Controleer uw internetverbinding en probeer het opnieuw.
      </Notice>
    );
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        KUVO biedt deskundigheidsbevordering aan voor uw team en leerkrachten: trainingen en workshops waarmee u zelf met cultuureducatie aan de slag gaat, kosteloos voor de leerkrachten en het team van uw school. Geef hieronder aan welke trainingen uw team graag wil volgen, dan stemt KUVO de invulling met u af. U bent nergens toe verplicht.
      </p>

      {/* Gratis info-alert (Origin UI alert-info-neutral): de trainingen zijn kosteloos. */}
      <div className="rounded-md border px-4 py-3">
        <p className="text-sm">
          <InfoIcon aria-hidden className="-mt-0.5 me-3 inline-flex text-blue-500" size={16} />
          Deze trainingen en workshops zijn kosteloos voor de leerkrachten en het team van uw school.
        </p>
      </div>

      {/* Per-school opt-out: a graceful "we doen dit jaar niet mee", so deze optionele stap netjes
          op afgerond komt te staan zonder dat er iets verplicht is. Aanvinken trekt eventueel
          eerder aangegeven interesse in; uitvinken opent de stap weer. */}
      <div className="rounded-lg border bg-muted/20 px-4 py-3">
        <label htmlFor={optOutId} className="flex cursor-pointer gap-3">
          <Checkbox
            id={optOutId}
            checked={optedOut}
            onCheckedChange={(val) => toggleOptOut(val === true)}
            disabled={togglingOptOut || saveState === "saving"}
            className="mt-0.5 shrink-0"
          />
          <span className="text-sm leading-5 text-foreground">
            Onze school neemt dit schooljaar geen deskundigheidsbevordering af.
          </span>
        </label>
      </div>

      {optedOut ? (
        <div className="rounded-lg border border-border bg-muted/30 px-6 py-8 text-center">
          <CheckCircle2Icon className="mx-auto text-emerald-600" size={22} />
          <p className="mt-2 text-sm text-muted-foreground">
            U heeft aangegeven dit schooljaar geen deskundigheidsbevordering af te nemen. Wilt u toch
            iets kiezen? Haal het vinkje hierboven weg, dan opent deze stap weer.
          </p>
        </div>
      ) : trainingOfferings.length === 0 ? (
        <div className="rounded-lg border border-border bg-muted/30 px-6 py-10 text-center">
          <InfoIcon className="mx-auto text-muted-foreground" size={24} />
          <p className="mt-3 text-sm text-muted-foreground">
            Er is op dit moment geen trainingsaanbod beschikbaar.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {sortedOfferings.map((offering) => {
            const programme = getProgramme(offering.programmeId);
            const provider = getProvider(offering.providerOrganizationId);
            const sel = interestByOffering.get(offering.id);
            const interested = !!sel;
            const title = programme?.title ?? offering.title;
            const description = programme?.shortDescription ?? offering.shortDescription;

            return (
              <div
                key={offering.id}
                className={`rounded-lg border p-4 ${interested ? "border-primary/30 bg-primary/5" : "border-border bg-card"}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {interested && <CheckCircle2Icon size={15} className="shrink-0 text-primary" />}
                      <h4 className="text-sm font-semibold text-foreground">{title}</h4>
                    </div>
                    {provider && (
                      <p className="mt-0.5 text-xs text-muted-foreground">{provider.displayName ?? provider.legalName}</p>
                    )}
                    {description && (
                      <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{description}</p>
                    )}
                  </div>
                  {interested ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0 text-destructive hover:text-destructive"
                      onClick={() => removeInterest(sel.id)}
                      disabled={busyId === sel.id || saveState === "saving"}
                    >
                      <Trash2Icon size={14} />
                      Intrekken
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => addInterest(offering)}
                      disabled={busyId === offering.id || saveState === "saving"}
                    >
                      <PlusIcon size={14} />
                      Interesse aangeven
                    </Button>
                  )}
                </div>

                {interested && sel && (
                  <div className="mt-3">
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">
                      Toelichting
                    </label>
                    <textarea
                      // Auto-growing (Origin UI Kit technique): field-sizing-content lets the box
                      // grow with its content; resize-none hides the manual handle; min-h-16 keeps a
                      // comfortable ~2-line start. No fixed rows, which would fight field-sizing.
                      className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background field-sizing-content min-h-16 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      value={notesDraft[sel.id] ?? sel.notes ?? ""}
                      onChange={(e) => setNotesDraft((prev) => ({ ...prev, [sel.id]: e.target.value }))}
                      onBlur={(e) => {
                        const next = e.target.value;
                        if (next !== (sel.notes ?? "")) saveNotes(sel.id, next);
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Bottom navigation */}
      <div className="flex items-center justify-between pt-5">
        <p className="text-xs text-muted-foreground">
          {optedOut
            ? "Geen deskundigheidsbevordering dit jaar; deze stap is afgerond."
            : interests.length > 0
              ? `${interests.length} ${interests.length === 1 ? "training" : "trainingen"} gekozen`
              : "Deskundigheidsbevordering is optioneel, u kunt gewoon doorgaan."}
        </p>
        <Button onClick={primaryAction.onClick} disabled={primaryAction.isConfirm && saveState === "saving"}>
          {primaryAction.label}
        </Button>
      </div>
    </div>
  );
}
